import { cn } from "@arcadeai/design-system/lib/utils";
import { Check } from "lucide-react";
import type { JSX, ReactNode } from "react";

export type OnboardingSidebarRowStatus = "active" | "complete" | "future";

const ICON_RING = {
  active: "border-2 border-brand-text text-brand-text",
  complete: "bg-brand-text/10 text-brand-text",
  future: "border border-border text-muted-foreground/40",
} as const;

type OnboardingSidebarRowProps = {
  icon: ReactNode;
  id: string;
  isLast: boolean;
  label: string;
  onClick?: () => void;
  recap?: ReactNode;
  status: OnboardingSidebarRowStatus;
};

export function OnboardingSidebarRow({
  id,
  label,
  icon,
  status,
  isLast,
  recap,
  onClick,
}: OnboardingSidebarRowProps): JSX.Element {
  const labelClass = cn("text-sm leading-snug", {
    "text-muted-foreground/40": status === "future",
    "font-medium text-foreground": status === "active",
    "text-muted-foreground": status === "complete",
  });

  const labelNode = onClick ? (
    <button
      aria-current={status === "active" ? "step" : undefined}
      className={cn(
        labelClass,
        "text-left hover:underline focus-visible:underline focus-visible:outline-none"
      )}
      onClick={onClick}
      type="button"
    >
      {label}
    </button>
  ) : (
    <span
      aria-current={status === "active" ? "step" : undefined}
      aria-disabled={status === "future" ? "true" : undefined}
      className={labelClass}
    >
      {label}
    </span>
  );

  return (
    <li className="flex w-full gap-4" id={id}>
      <div className="flex w-8 shrink-0 flex-col items-center self-stretch">
        <div
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-full",
            ICON_RING[status]
          )}
        >
          {status === "complete" ? <Check className="size-4" /> : icon}
        </div>
        {!isLast && (
          <div
            aria-hidden="true"
            className="my-2 min-h-12 w-px flex-1 bg-border"
          />
        )}
      </div>

      <div
        className={cn("flex min-w-0 flex-1 flex-col pt-1", !isLast && "pb-2")}
      >
        {labelNode}
        {recap && status !== "future" && (
          <div className="mt-2.5 w-full min-w-0">{recap}</div>
        )}
      </div>
    </li>
  );
}
